import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import api from "../services/api";
import DomainFields, { mergeCompetitors } from "../components/DomainFields";
import { toHostname } from "../utils/targetInputs";
import { useBreadcrumb } from "../hooks/useBreadcrumb";
import Spinner from "../shared/shared-component/Spinner";
import useProjectStore from "../store/useProjectStore";
import CmsConnection from "./CmsConnection";
// Must match PROJECT_CONTEXT_MAX_LENGTH in backend/models/Project.js
const PROJECT_CONTEXT_MAX_LENGTH = 2000;

// Default until /projects/locations responds; the server owns the real list.
const GLOBAL_LOCATION = "Global";

function ConfiguredBadge({ configured, label }) {
  return configured ? (
    <span className="successConfigure">
      ✓ {label || "Configured"} — enter a new value to replace
    </span>
  ) : (
    <span className="wrapper__configure fb__11 mt__5 d-flex fc__5F636A">
      Not configured
    </span>
  );
}

function DeleteConfirmModal({ projectName, onConfirm, onCancel, deleting }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid rgba(239,68,68,0.3)",
          borderRadius: "14px",
          padding: "28px",
          width: "100%",
          maxWidth: "420px",
        }}
      >
        {/* Icon + title */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M3 5h12M7 5V3h4v2M6 5v9a1 1 0 001 1h4a1 1 0 001-1V5H6z"
                stroke="#f87171"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div>
            <div
              style={{
                fontSize: "15px",
                fontWeight: "700",
                color: "var(--text-primary)",
              }}
            >
              Delete project?
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "var(--text-muted)",
                marginTop: "2px",
              }}
            >
              This action cannot be undone
            </div>
          </div>
        </div>

        {/* Warning body */}
        <div
          style={{
            padding: "14px",
            borderRadius: "10px",
            background: "rgba(239,68,68,0.06)",
            border: "1px solid rgba(239,68,68,0.15)",
            marginBottom: "20px",
          }}
        >
          <p
            style={{
              margin: "0 0 8px",
              fontSize: "13px",
              color: "var(--text-secondary)",
              lineHeight: "1.6",
            }}
          >
            You are about to delete{" "}
            <strong style={{ color: "var(--text-primary)" }}>
              {projectName}
            </strong>
            .
          </p>
          <p
            style={{
              margin: 0,
              fontSize: "13px",
              color: "#f87171",
              lineHeight: "1.6",
            }}
          >
            All executions run under this project will also be deleted.
          </p>
        </div>

        {/* Actions */}
        <div
          style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={deleting}
            style={{
              padding: "9px 18px",
              background: "transparent",
              border: "1px solid var(--border)",
              borderRadius: "8px",
              color: "var(--text-muted)",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            style={{
              padding: "9px 18px",
              background: "rgba(239,68,68,0.15)",
              border: "1px solid rgba(239,68,68,0.35)",
              borderRadius: "8px",
              color: "#f87171",
              fontSize: "13px",
              fontWeight: "600",
              cursor: deleting ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {deleting && (
              <span
                className="spinner"
                style={{
                  width: "12px",
                  height: "12px",
                  borderWidth: "2px",
                  borderColor: "#f87171",
                  borderTopColor: "transparent",
                }}
              />
            )}
            {deleting ? "Deleting…" : "Delete Project"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectForm({ project, onSaved, onDeleted, onActivated }) {
  const isNew = !project?.id;

  const [name, setName] = useState(project?.name || "");
  // Locked values live on `project`; these hold only what the user is adding.
  const [domain, setDomain] = useState("");
  const [competitors, setCompetitors] = useState(["", ""]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [context, setContext] = useState(project?.context || "");
  const [targetLocation, setTargetLocation] = useState(
    project?.targetLocation || "United States",
  );
  const [locationOptions, setLocationOptions] = useState(["United States"]);
  const [googleCredentials, setGoogleCredentials] = useState("");
  const [googleApiKey, setGoogleApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Reset form when project changes
  useEffect(() => {
    setName(project?.name || "");
    setDomain("");
    setCompetitors(["", ""]);
    setFieldErrors({});
    setContext(project?.context || "");
    setTargetLocation(project?.targetLocation || "United States");
    setGoogleCredentials("");
    setGoogleApiKey("");
    setError("");
    setSuccess("");
  }, [project?.id]);

  // Load the canonical location list once. On failure the dropdown still offers
  // Global, so the form stays usable rather than rendering an empty select.
  useEffect(() => {
    let cancelled = false;
    api
      .getProjectLocations()
      .then((res) => {
        if (!cancelled && Array.isArray(res.data?.locations)) {
          setLocationOptions(res.data.locations);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // What the project falls back to when the name is cleared.
  const derivedName = project?.domain || toHostname(domain);

  const handleCompetitorChange = (index, value) => {
    setCompetitors((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setFieldErrors((prev) => ({ ...prev, [`competitor${index}`]: "" }));
  };

  // Client-side pre-flight for the write-once domain fields. The server
  // re-validates and owns the locking rules.
  const validateDomains = () => {
    const next = {};
    const savedDomain = project?.domain || null;
    const savedRivals = project?.competitors || [];
    const mainHost = savedDomain || toHostname(domain);

    if (!savedDomain) {
      if (!domain.trim()) {
        next.domain = "Main domain is required";
      } else if (!mainHost.includes(".")) {
        next.domain = "Enter a valid domain, e.g. acme.com";
      }
    }

    const seen = new Set([mainHost, ...savedRivals].filter(Boolean));
    competitors.forEach((c, i) => {
      if (savedRivals[i] || !c.trim()) return;
      const host = toHostname(c);
      if (!host.includes(".")) {
        next[`competitor${i}`] = "Enter a valid domain, e.g. rival.com";
      } else if (seen.has(host)) {
        next[`competitor${i}`] =
          host === mainHost
            ? "A competitor cannot be your main domain"
            : "This domain is already listed";
      } else {
        seen.add(host);
      }
    });

    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateDomains()) {
      setError("");
      setSuccess("");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const nextCompetitors = mergeCompetitors(
        project?.competitors || [],
        competitors,
      );
      const payload = {
        name: name.trim(),
        context,
        targetLocation,
        // Only send the main domain when it is actually being set — the server
        // rejects an attempt to change one that already exists.
        ...(project?.domain ? {} : { domain: domain.trim() }),
        // Only send competitors when something new is being added, so a plain
        // "Save Changes" never touches the locked list.
        ...(nextCompetitors.length > (project?.competitors?.length || 0)
          ? { competitors: nextCompetitors }
          : {}),
        ...(googleCredentials
          ? { googleCredentialsJson: googleCredentials }
          : {}),
        ...(googleApiKey ? { googleApiKey } : {}),
      };

      let res;
      if (isNew) {
        res = await api.createProject(payload);
      } else {
        res = await api.updateProject(project.id, payload);
      }
      setSuccess(isNew ? "Project created" : "Changes saved");
      setGoogleCredentials("");
      setGoogleApiKey("");
      // Newly added domains are now locked and come back on the project, so
      // clear the "adding" inputs rather than leaving them duplicated.
      setDomain("");
      setCompetitors(["", ""]);
      onSaved(res.data.project);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.deleteProject(project.id);
      setShowDeleteConfirm(false);
      onDeleted(project.id);
    } catch (err) {
      setShowDeleteConfirm(false);
      setError(err.response?.data?.error || err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleActivate = async () => {
    try {
      await api.activateProject(project.id);
      onActivated(project.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  return (
    <>
      {showDeleteConfirm && (
        <DeleteConfirmModal
          projectName={project.name}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          deleting={deleting}
        />
      )}
      <form onSubmit={handleSave}>
        {error && (
          <div className="alert alert-error mb__16">
            <span>⚠</span> {error}
          </div>
        )}
        {success && <div className="successMsg fb__13 mb__16">✓ {success}</div>}

        {/* Main domain + competitors — write-once, see DomainFields */}
        <div className="domain mb__16">
          <h3 className="fb__12 fw__700 fc__5F636A uppercase">Domains</h3>
        </div>
        <DomainFields
          savedDomain={project?.domain || null}
          savedCompetitors={project?.competitors || []}
          domain={domain}
          competitors={competitors}
          onDomainChange={(v) => {
            setDomain(v);
            setFieldErrors((p) => ({ ...p, domain: "" }));
          }}
          onCompetitorChange={handleCompetitorChange}
          disabled={saving}
          errors={fieldErrors}
        />

        {/* Name — optional, falls back to the domain server-side */}
        <div className="wrapper__sec mb__20">
          <label className="form__label">Project Name</label>
          <p className="fb__11 fc__5F636A mb__8">
            Just a label for your own reference. Leave it blank to name the
            project after its main domain.
          </p>
          <input
            className="form__input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={derivedName || "e.g. Client A — Main Site"}
            maxLength={80}
            disabled={saving}
          />
        </div>

        {/* Google Search Console */}
        <div className="wrapper__sec mb__20">
          <div className="form__label">
            Google Search Console [GSC] (Optional)
          </div>
          <p className="fb__11 fc__5F636A mb__8">
            Paste the full contents of your Google Service Account JSON file.
            This is used by the <span className="code">mcp-server-gsc</span> MCP
            server to connect Claude Code to Search Console.
          </p>
          <label className="form__label">Service Account JSON</label>
          <textarea
            className="form__input code__input"
            value={googleCredentials}
            onChange={(e) => setGoogleCredentials(e.target.value)}
            placeholder={
              '{\n  "type": "service_account",\n  "project_id": "...",\n  ...\n}'
            }
          />
          {!isNew && (
            <ConfiguredBadge
              configured={project?.hasGoogleCredentials}
              label="GSC credentials configured"
            />
          )}
        </div>

        {/* Google API Key */}
        <div className="wrapper__sec mb__20">
          <div className="form__label">Google API Key (optional)</div>
          <p className="fb__11 fc__5F636A mb__8">
            Used for Google Pagespeed, CrUX, and other Google API integrations.
            Get one from{" "}
            <span className="fc__E57600">console.cloud.google.com</span>.
          </p>
          <label className="form__label">API Key</label>
          <input
            type="password"
            className="form__input"
            value={googleApiKey}
            onChange={(e) => setGoogleApiKey(e.target.value)}
            placeholder="AIza..."
            autoComplete="off"
          />
          {!isNew && (
            <ConfiguredBadge
              configured={project?.hasGoogleApiKey}
              label="API key configured"
            />
          )}
        </div>

        {/* Target location — fed to the agents on every run */}
        <div className="wrapper__sec mb__20">
          <label className="form__label">Target Location</label>
          <p className="fb__11 fc__5F636A mb__8">
            The market audits run for — SERP checks, rankings, keyword volumes
            and AI-answer visibility all reflect this country.{" "}
            <span className="fw__700">Global</span> sets no target market and
            leaves each agent on its own default.
          </p>
          <select
            className="form__input form__select"
            value={targetLocation}
            onChange={(e) => setTargetLocation(e.target.value)}
          >
            {/* Keep the saved value selectable even if the list fails to load */}
            {(locationOptions.includes(targetLocation)
              ? locationOptions
              : [targetLocation, ...locationOptions]
            ).map((loc) => (
              <option key={loc} value={loc}>
                {loc === GLOBAL_LOCATION ? `${loc} (no specific market)` : loc}
              </option>
            ))}
          </select>
        </div>

        {/* Project context — fed to the agents on every run */}
        <div className="wrapper__sec mb__20">
          <label className="form__label">Project Context (optional)</label>
          <p className="fb__11 fc__5F636A mb__8">
            Sent to the AI with every analysis of your main domain. Describe the
            business, audience, locations, brand and competitor names, tech
            stack, and priority pages — the agents use it instead of guessing
            from the URL. This is read by the AI, so keep internal remarks out
            of it.
          </p>
          <p className="fb__12 fc__5F636A mb__8">
            Applies to your main domain only — it is not sent when an agent is
            run against a competitor.
          </p>
          <textarea
            className="form__input code__input"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            maxLength={PROJECT_CONTEXT_MAX_LENGTH}
            placeholder={
              "e.g. B2B SaaS for logistics teams in the US and Canada. " +
              "Brand: Acme. Competitors: Bolt, Cargowise. " +
              "Next.js, client-side rendered. Priority pages: /pricing, /platform."
            }
          />
          <div className="fb__11 mt__5 fc__5F636A text__right">
            {context.length}/{PROJECT_CONTEXT_MAX_LENGTH}
          </div>
        </div>

        {/* Actions */}
        <div className="form__submit d-flex align-items-center justify-content-between">
          <div className="d-flex gap__8">
            <button
              type="submit"
              className="common__btn common__org__btn"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner size="small" />
                  Saving...
                </>
              ) : isNew ? (
                "Create Project"
              ) : (
                "Save Changes"
              )}
            </button>
            {/* {!isNew && !project?.isDefault && (
            <button
              type="button"
              className="btn btn-small"
              onClick={handleActivate}
              style={{
                background: "rgba(16,185,129,0.1)",
                border: "1px solid rgba(16,185,129,0.25)",
                color: "#34d399",
              }}
            >
              ★ Set as Active
            </button>
          )} */}
          </div>
          {/* Deleting a project with a locked domain would be a way around that
            lock (delete, re-create elsewhere), so it is an admin action. */}
          {!isNew &&
            (project?.domain ? (
              <span className="fb__11 text__right mw__230 fc__5F636A">
                Contact your administrator to delete this project or change its
                domains.
              </span>
            ) : (
              <button
                type="button"
                className="common__btn common__org__btn"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </button>
            ))}
        </div>
      </form>
    </>
  );
}

export default function SettingsPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlProjectId = searchParams.get("projectId");
  const urlNew = searchParams.get("new") === "true";

  const [projects, setProjects] = useState([]);
  const [quota, setQuota] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(urlNew);
  const [loadError, setLoadError] = useState(false);

  // NEW: copy the local list into the store
  const setStoreProjects = useProjectStore((s) => s.setProjects);

  useEffect(() => {
    if (loading || loadError) return;
    setStoreProjects(projects);
  }, [projects, loading, loadError, setStoreProjects]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await api.getProjects();
      const list = res.data.projects || [];
      setProjects(list);
      setQuota(res.data.quota || null);
      if (!selectedId && list.length > 0 && !urlNew) {
        // Honour the projectId URL param (e.g. from "Go to Settings" credential
        // warning), then fall back to the default project, then the first one.
        const fromUrl = urlProjectId && list.find((p) => p.id === urlProjectId);
        setSelectedId(
          fromUrl?.id || list.find((p) => p.isDefault)?.id || list[0].id,
        );
      }
    } catch (err) {
      console.error("Failed to load projects:", err);
      setLoadError(true);
      // optionally: setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedId, urlProjectId, urlNew]);

  useBreadcrumb(
    [{ label: "Workspace", to: "/mainScreen" }, { label: "Projects" }],
    [location.pathname],
  );

  useEffect(() => {
    load();
  }, []); // load is stable — only runs on mount

  const handleSaved = (updatedProject) => {
    setProjects((prev) => {
      const exists = prev.find((p) => p.id === updatedProject.id);
      if (exists)
        return prev.map((p) =>
          p.id === updatedProject.id ? updatedProject : p,
        );
      // A new project consumes a quota slot — re-read so the sidebar's
      // "New Project" affordance reflects it immediately.
      setQuota((q) =>
        q
          ? { ...q, used: q.used + 1, canCreate: q.used + 1 < q.maxProjects }
          : q,
      );
      return [...prev, updatedProject];
    });
    setSelectedId(updatedProject.id);
    setShowNewForm(false);
  };

  const handleDeleted = (id) => {
    setProjects((prev) => {
      const next = prev.filter((p) => p.id !== id);
      setSelectedId(next[0]?.id || null);
      return next;
    });
    setQuota((q) =>
      q
        ? {
            ...q,
            used: Math.max(0, q.used - 1),
            canCreate: Math.max(0, q.used - 1) < q.maxProjects,
          }
        : q,
    );
    setShowNewForm(false);
  };

  const handleActivated = (id) => {
    setProjects((prev) => prev.map((p) => ({ ...p, isDefault: p.id === id })));
  };

  const selectedProject = projects.find((p) => p.id === selectedId);

  return (
    <section className="wrapper__execution__history wrapper__main">
      <div className="head mb__32">
        <h2 className="h2 mb__6 fc__0E1218 fw__700">Projects</h2>
        <p className="fb__16 fc__5F636A">
          Connect Google Search Console and API credentials per project.
        </p>
      </div>
      <div className="wrapper__project__setting__grid custom__row">
        {/* Sidebar — project list */}
        <div className="wrapper__project__left">
          <div className="wrapper__project__listing">
            <div className="head mb__16">
              <h2 className="fb__14 fw__600 fc__5F636A ls__1__2">Projects</h2>
            </div>
            <div className="wrapper__buttons">
              {loading ? (
                <Spinner size="small" />
              ) : loadError ? (
                <div className="wraper__noProject">
                  <p className="fb__13 fw__500 fc__5F636A">
                    Couldn't load projects
                  </p>
                </div>
              ) : projects.length === 0 ? (
                <div className="wraper__noProject">
                  <p className="fb__13 fw__500 fc__5F636A">No projects yet</p>
                </div>
              ) : (
                <React.Fragment>
                  {projects.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => {
                        setSelectedId(p.id);
                        setShowNewForm(false);
                      }}
                      className={
                        selectedId === p.id && !showNewForm
                          ? "project__btn selected"
                          : "project__btn"
                      }
                    >
                      <span
                        className={
                          selectedId === p.id && !showNewForm
                            ? "project__name active"
                            : "project__name"
                        }
                      >
                        {p.name}
                      </span>
                      {p.domain ? (
                        p.domain !== p.name && (
                          <span className="domian fb__12 fw__500 fc__5F636A">
                            {p.domain}
                          </span>
                        )
                      ) : (
                        <span className="noDomain fb__12 fc__ECAA0B ">
                          ⚠ No domain set
                        </span>
                      )}
                      {p.isDefault && (
                        <span className="default__project fb__10 fw__600 fc__007A55">
                          ★ Active
                        </span>
                      )}
                    </button>
                  ))}
                </React.Fragment>
              )}
            </div>
            {!loadError && (
              <div className="wraper__newProject__limit">
                {quota && !quota.canCreate ? (
                  <div
                    className={
                      projects.length > 0
                        ? "project__quota active"
                        : "project__quota"
                    }
                  >
                    <p className="fb__12 fc__E52F0F">
                      {quota.used} of {quota.maxProjects} project
                      {quota.maxProjects > 1 ? "s" : ""} used. Contact your
                      administrator to raise the limit.
                    </p>
                  </div>
                ) : (
                  <button
                    className={
                      showNewForm
                        ? "wrapper__createProject active"
                        : "wrapper__createProject"
                    }
                    onClick={() => {
                      setShowNewForm(true);
                      setSelectedId(null);
                    }}
                  >
                    + New Project
                    {quota ? ` (${quota.used}/${quota.maxProjects})` : ""}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main content — form */}
        <div className="wrapper__project__right">
          <div className="wrapper__project__details ">
            {showNewForm ? (
              <>
                <h3 className="fb__16 fw__700 fc__0E1218 mb__20">
                  New Project
                </h3>
                <div className="wrapper__project__form bg__FFFFFF">
                  <ProjectForm
                    project={null}
                    onSaved={handleSaved}
                    onDeleted={handleDeleted}
                    onActivated={handleActivated}
                  />
                </div>
              </>
            ) : selectedProject ? (
              <>
                <div className="head d-flex align-items-center gap__8 mb__22">
                  <h3 className="fb__16 fw__700 fc__0E1218 ">
                    {selectedProject.name}
                  </h3>
                  {selectedProject.isDefault && (
                    <span className="default__project fb__10 fw__600 fc__007A55">
                      ★ Active Project
                    </span>
                  )}
                </div>
                <div className="wrapper__project__form bg__FFFFFF">
                  <ProjectForm
                    key={selectedProject.id}
                    project={selectedProject}
                    onSaved={handleSaved}
                    onDeleted={handleDeleted}
                    onActivated={handleActivated}
                  />
                </div>

                {/* Connecting a site needs a domain to connect TO, and the
                    domain is write-once, so this only appears once the
                    project is complete. */}
                {selectedProject.domain && (
                  <div style={{ marginTop: 28 }}>
                    <h3 className="fb__16 fw__700 fc__0E1218 mb__20">
                      Applying changes to your site
                    </h3>
                    <CmsConnection
                      key={`cms-${selectedProject.id}`}
                      project={selectedProject}
                    />
                  </div>
                )}
              </>
            ) : loadError ? (
              <div className="error-page text__center mw__672" role="alert">
                <h1 className="h2 fc__0E1218 mb__16">
                  Couldn't load your projects
                </h1>
                <p className="fb__16 fc__5F636A mb__10">
                  Please check your internet connection and try again.
                </p>
                <button
                  className="common__btn common__transp__btn"
                  onClick={load}
                >
                  Retry
                </button>
              </div>
            ) : !loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  color: "var(--text-muted)",
                }}
              >
                <div
                  style={{
                    fontSize: "32px",
                    marginBottom: "12px",
                    opacity: 0.5,
                  }}
                >
                  🔑
                </div>
                <p className="fb__12 fc__5F636A">
                  Create your first project to configure API keys
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
